import { useModal } from "@/app/shared/modal-views/use-modal";
import { mapCategory } from "@/utils/category-mapping";
import { CSVPlaceData } from "@/utils/csv-validation";
import { useCallback } from "react";
import { PiCheckBold } from "react-icons/pi";
import { Button, Text, Badge } from "rizzui";
import { Table } from "rizzui";

interface ReviewCSVModalProps {
  csvData: CSVPlaceData[];
  isImporting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ReviewCSVModal({
  csvData,
  onConfirm,
  onCancel,
  isImporting,
}: ReviewCSVModalProps) {
  const { closeModal, isOpen } = useModal();

  const totalRecords = csvData.length;

  const handleConfirm = useCallback(() => {
    onConfirm();
    if (isOpen) {
      closeModal();
    }
  }, [onConfirm, isOpen, closeModal]);

  const handleCancel = useCallback(() => {
    onCancel();
    if (isOpen) {
      closeModal();
    }
  }, [onCancel, isOpen, closeModal]);

  return (
    <div className="@container max-w-6xl max-h-[90vh] overflow-hidden flex flex-col mt-4">
      <div className="flex-1 overflow-hidden flex flex-col gap-6">
        {/* Data Preview Table */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-4">
            <Text className="font-medium">Data Preview</Text>
            <Text className="text-sm text-gray-600">
              Showing first {Math.min(csvData.length, 10)} of {totalRecords}{" "}
              records
            </Text>
          </div>

          <div className="border rounded-lg overflow-hidden flex-1">
            <div className="overflow-auto max-h-96">
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>Name</Table.Head>
                    <Table.Head>Category</Table.Head>
                    <Table.Head>Latitude</Table.Head>
                    <Table.Head>Longitude</Table.Head>
                    <Table.Head>Status</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {csvData.map((row, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <div className="max-w-xs">
                          <Text className="truncate" title={row.name}>
                            {row.name}
                          </Text>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        {row.category ? (
                          <Badge color="secondary" className="text-xs">
                            {mapCategory(row.category) || "-"}
                          </Badge>
                        ) : (
                          <Text className="text-gray-400 text-base font-medium">-</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Text className="font-mono text-sm">
                          {row.latitude}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text className="font-mono text-sm">
                          {row.longitude}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color="success" className="text-xs">
                          Valid
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="w-auto"
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            isLoading={isImporting}
            className="w-auto bg-green-600 hover:bg-green-700"
          >
            <PiCheckBold className="mr-2 h-4 w-4" />
            {isImporting
              ? "Importing..."
              : `Import`}
          </Button>
        </div>
      </div>
    </div>
  );
}
